"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAnyspendCreateOnrampOrder, useGeoOnrampOptions } from "../../../../anyspend/react/index.js";
import { anyspendService } from "../../../../anyspend/services/anyspend.js";
import { formatTokenAmount } from "../../../../shared/utils/number.js";
import { ShinyButton, TextShimmer, useTokenData } from "../../../../global-account/react/index.js";
import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
/** Map chain IDs to Coinbase blockchain identifiers */
const CHAIN_ID_TO_COINBASE_BLOCKCHAIN = {
  1: "ethereum",
  8453: "base",
  42161: "arbitrum",
  10: "optimism",
  137: "polygon",
  43114: "avalanche",
  56: "bsc"
};
export function CoinbaseCheckoutPanel({
  recipientAddress,
  destinationTokenAddress,
  destinationTokenChainId,
  totalAmount,
  themeColor,
  onSuccess,
  onOrderCreated,
  onError,
  callbackMetadata
}) {
  // Stable refs for callback props to avoid re-triggering effects
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onOrderCreatedRef = useRef(onOrderCreated);
  onOrderCreatedRef.current = onOrderCreated;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const {
    data: tokenData
  } = useTokenData(destinationTokenChainId, destinationTokenAddress);
  const formattedAmount = useMemo(() => {
    const decimals = tokenData?.decimals || 18;
    return formatTokenAmount(BigInt(totalAmount), decimals);
  }, [totalAmount, tokenData]);
  const {
    geoData,
    coinbaseAvailablePaymentMethods,
    isLoading
  } = useGeoOnrampOptions(formattedAmount);
  // Order + redirect state
  const [orderId, setOrderId] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(null);
  const orderCreatedRef = useRef(false);
  const {
    createOrder,
    isCreatingOrder
  } = useAnyspendCreateOnrampOrder({
    onSuccess: data => {
      const id = data.data?.id;
      if (id) {
        setOrderId(id);
      } else {
        setError("Failed to create Coinbase order. Please try again.");
      }
    },
    onError: err => {
      setError(err.message || "Failed to create payment order.");
      onErrorRef.current?.(err);
    }
  });
  // Once we have orderId, generate Coinbase onramp URL and redirect
  useEffect(() => {
    if (!orderId || isRedirecting) return;
    const blockchain = CHAIN_ID_TO_COINBASE_BLOCKCHAIN[destinationTokenChainId];
    if (!blockchain) {
      setError(`Coinbase Pay is not supported for this chain.`);
      return;
    }
    const paymentMethodId = coinbaseAvailablePaymentMethods[0]?.id || "CARD";
    setIsRedirecting(true);
    anyspendService.getCoinbaseOnrampUrl({
      presetFiatAmount: formattedAmount,
      fiatCurrency: "USD",
      defaultAsset: tokenData?.symbol || "USDC",
      defaultPaymentMethod: paymentMethodId,
      destinationAddress: recipientAddress,
      blockchains: [blockchain],
      country: geoData?.country || "US",
      orderId,
      redirectUrl: window.location.href,
      useSessionToken: false
    }).then(response => {
      if (response.data?.url) {
        // Persist orderId directly before redirect — useEffect won't run in time
        if (orderId) {
          sessionStorage.setItem("anyspend_checkout_orderId", orderId);
          onOrderCreatedRef.current?.(orderId);
        }
        onSuccessRef.current?.({
          orderId
        });
        window.location.href = response.data.url;
      } else {
        setError("Failed to generate Coinbase Pay URL.");
        setIsRedirecting(false);
      }
    }).catch(err_0 => {
      setError(err_0.message || "Failed to generate Coinbase Pay URL.");
      setIsRedirecting(false);
      onErrorRef.current?.(err_0 instanceof Error ? err_0 : new Error(err_0.message));
    });
  }, [orderId, isRedirecting, destinationTokenChainId, coinbaseAvailablePaymentMethods, formattedAmount, tokenData, recipientAddress, geoData]);
  const handleContinue = useCallback(() => {
    if (isCreatingOrder || orderCreatedRef.current) return;
    setError(null);
    orderCreatedRef.current = true;
    const dstToken = {
      address: destinationTokenAddress,
      chainId: destinationTokenChainId,
      decimals: tokenData?.decimals || 18,
      symbol: tokenData?.symbol || "",
      name: tokenData?.name || "",
      metadata: {
        logoURI: tokenData?.logoURI || ""
      }
    };
    const paymentMethodId_0 = coinbaseAvailablePaymentMethods[0]?.id || "CARD";
    createOrder({
      recipientAddress,
      orderType: "swap",
      dstChain: destinationTokenChainId,
      dstToken,
      srcFiatAmount: formattedAmount,
      onramp: {
        vendor: "coinbase",
        paymentMethod: paymentMethodId_0,
        country: geoData?.country || "US",
        redirectUrl: window.location.href
      },
      expectedDstAmount: totalAmount,
      callbackMetadata
    });
  }, [isCreatingOrder, destinationTokenAddress, destinationTokenChainId, tokenData, coinbaseAvailablePaymentMethods, recipientAddress, formattedAmount, geoData, totalAmount, callbackMetadata, createOrder]);
  if (isLoading) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        ease: "easeOut"
      },
      className: "anyspend-coinbase-loading flex items-center justify-center py-6",
      children: [_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "ml-2 text-sm",
        children: "Checking availability..."
      })]
    });
  }
  if (coinbaseAvailablePaymentMethods.length === 0) {
    return _jsx(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: "anyspend-coinbase-unavailable py-4 text-center",
      children: _jsx("p", {
        className: "text-sm text-gray-500 dark:text-gray-400",
        children: "Coinbase Pay is not available in your region for this amount."
      })
    });
  }
  const isProcessing = isCreatingOrder || isRedirecting;
  return _jsxs(motion.div, {
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: "easeOut"
    },
    className: "anyspend-coinbase-panel flex flex-col gap-3",
    children: [_jsx("p", {
      className: "anyspend-coinbase-description text-sm text-gray-600 dark:text-gray-400",
      children: "Pay with your Coinbase account using debit card, bank account, or crypto balance."
    }), _jsx(AnimatePresence, {
      initial: false,
      children: error && _jsx(motion.div, {
        initial: {
          opacity: 0,
          height: 0
        },
        animate: {
          opacity: 1,
          height: "auto"
        },
        exit: {
          opacity: 0,
          height: 0
        },
        transition: {
          duration: 0.2,
          ease: "easeOut"
        },
        style: {
          overflow: "hidden"
        },
        className: "rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20",
        children: _jsx("p", {
          className: "text-sm text-red-600 dark:text-red-400",
          children: error
        })
      }, "coinbase-error")
    }), _jsx(ShinyButton, {
      accentColor: themeColor || "#0052FF",
      onClick: () => {
        if (error) {
          // Reset state for retry
          setError(null);
          setOrderId(null);
          setIsRedirecting(false);
          orderCreatedRef.current = false;
        }
        handleContinue();
      },
      disabled: isProcessing,
      className: "anyspend-coinbase-btn w-full",
      textClassName: "text-white",
      children: isProcessing ? _jsxs("span", {
        className: "flex items-center justify-center gap-2",
        children: [_jsx(Loader2, {
          className: "h-4 w-4 animate-spin"
        }), isRedirecting ? "Redirecting to Coinbase..." : "Creating order..."]
      }) : error ? "Try again" : "Continue with Coinbase"
    })]
  });
}